"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Detects scroll direction with a threshold to avoid jitter.
 * Returns `true` when scrolling down, `false` when scrolling up or near the top.
 *
 * Near the top of the page (within `topThreshold`), always returns `false`
 * so sticky elements remain visible.
 */
export function useScrollDirection({
  threshold = 10,
  topThreshold = 100,
}: {
  /** Minimum scroll delta before direction flips (px). */
  threshold?: number;
  /** Always report "up" when scrollY is below this value (px). */
  topThreshold?: number;
} = {}) {
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const onScroll = () => {
      if (ticking.current) {
        return;
      }
      ticking.current = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;

        // Near the top — always show
        if (currentY < topThreshold) {
          setIsScrollingDown(false);
          lastScrollY.current = currentY;
          ticking.current = false;
          return;
        }

        const diff = currentY - lastScrollY.current;

        if (Math.abs(diff) >= threshold) {
          setIsScrollingDown(diff > 0);
          lastScrollY.current = currentY;
        }

        ticking.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, topThreshold]);

  return isScrollingDown;
}
